import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import Icon from '../../../components/AppIcon';
import { siteDiaryService } from '../../../services/siteDiaryService';
import PhotoDeleteConfirmModal from './PhotoDeleteConfirmModal';

/**
 * Renders thumbnails for an entry's diary photos and a lightbox on click.
 * Fetches short-lived signed URLs lazily — only when the gallery mounts
 * (i.e. when the user expands an entry), so collapsed entries never pay
 * the cost of generating N signed URLs they won't see.
 *
 * NSherjFh PR 12 — adds per-photo hard-delete with storage cleanup.
 * Delete affordance is hover-only (mirrors the NotesFeed pattern from
 * PR 11) and gated by the caller via `canDeletePhoto`. The service
 * call lives in this component (same shape as NotesFeed.handleDelete:
 * leaf component owns the call, parent owns the list state via
 * `onPhotoDeleted` callback). Errors are surfaced via toast and the
 * photo is left in the UI so the user can retry.
 *
 * Props:
 *   photos: array of { id, file_path, uploaded_by } from daily_site_photos
 *   canDeletePhoto?: (photo) => boolean — when omitted or returns false,
 *     no Delete button renders on that photo. PR 12 callers pass
 *     `isAdminOrManager || photo.uploaded_by === currentUserId` per the
 *     RLS shape (admin_site_manager_full_access_diary_photos +
 *     workers_manage_own_daily_photos).
 *   onPhotoDeleted?: (photoId) => void — called after a successful
 *     delete so the parent can drop the photo from its entries state.
 */
const DiaryPhotoGallery = ({ photos, canDeletePhoto, onPhotoDeleted }) => {
  const [urls, setUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  // NSherjFh PR 12 — one modal instance per gallery, with the target
  // photo tracked in state. Per-photo modal instances would render
  // N early-returns on first paint (cheap, but noisy in devtools).
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!photos?.length) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      const paths = photos?.map(p => p?.file_path)?.filter(Boolean);
      const { data } = await siteDiaryService?.getPhotoUrls(paths);
      if (!cancelled) {
        setUrls(data || {});
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [photos]);

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.id) return;
    const target = deleteTarget;
    setIsDeleting(true);
    const { error } = await siteDiaryService?.deleteDailySitePhoto(target?.id);
    setIsDeleting(false);
    if (error) {
      // Non-optimistic: leave the photo in the UI so the user can
      // retry. Mirrors PR 11 NotesFeed.handleDelete (rationale: a
      // failed delete that disappears from UI but is still in
      // storage/DB diverges client state from server state).
      console.error('[DiaryPhotoGallery] delete failed:', {
        photoId: target?.id,
        file_path: target?.file_path,
        code: error?.code,
        message: error?.message,
      });
      const msg =
        error?.code === 'PGRST301' || error?.code === '42501'
          ? `Permission denied deleting photo: ${error?.message ?? `code ${error?.code}`}`
          : `Failed to delete photo: ${error?.message ?? 'unknown error'}`;
      toast?.error(msg);
      setDeleteTarget(null);
      return;
    }
    // Success — close modal, drop the cached URL so future renders
    // don't keep a stale entry, and notify parent.
    setUrls(prev => {
      const next = { ...prev };
      delete next[target?.file_path];
      return next;
    });
    setDeleteTarget(null);
    onPhotoDeleted?.(target?.id);
    toast?.success('Photo deleted');
  };

  if (!photos?.length) return null;

  return (
    <>
      <div>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
          Photos ({photos?.length})
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {photos?.map((photo) => {
            const url = urls?.[photo?.file_path];
            const showDelete = canDeletePhoto?.(photo);
            return (
              <div
                key={photo?.id}
                className="relative aspect-square rounded-lg overflow-hidden border border-border bg-muted cursor-pointer hover:opacity-80 transition-opacity group"
                onClick={() => url && setLightboxUrl(url)}
              >
                {loading ? (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon name="Loader2" size={18} className="text-muted-foreground animate-spin" />
                  </div>
                ) : url ? (
                  <img
                    src={url}
                    alt="Diary photo"
                    className="w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Icon name="ImageOff" size={18} className="text-muted-foreground" />
                  </div>
                )}

                {/* NSherjFh PR 12 — hover-only hard-delete affordance.
                    e.stopPropagation prevents the parent thumbnail's
                    lightbox-open click handler from also firing. */}
                {showDelete && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e?.stopPropagation();
                      setDeleteTarget(photo);
                    }}
                    className="absolute top-1 right-1 w-7 h-7 rounded-md flex items-center justify-center bg-background/80 backdrop-blur-sm text-muted-foreground hover:text-destructive hover:bg-background opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Delete photo"
                    aria-label="Delete photo"
                  >
                    <Icon name="Trash2" size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            onClick={(e) => { e?.stopPropagation(); setLightboxUrl(null); }}
            aria-label="Close"
          >
            <Icon name="X" size={20} className="text-white" />
          </button>
          <img src={lightboxUrl} alt="Full size" className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}

      {/* NSherjFh PR 12 — hard-delete confirm modal. */}
      <PhotoDeleteConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => { if (!isDeleting) setDeleteTarget(null); }}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default DiaryPhotoGallery;
